import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../axiosInstance";
import { useAuth } from "../../contexts/AuthContext";
import "./Clients.scss";
import { toast } from "react-toastify";
import IconButton from "../../components/IconButton/IconButton";
import GeneralTable from "../../components/Table/GeneralTable";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";
import WarningPopup from "../../components/WarningPopup/WarningPopup";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import { Icon } from "@iconify/react/dist/iconify.js";

const Clients = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingClientId, setEditingClientId] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignClientId, setReassignClientId] = useState(null);
  const [selectedReassignEmployeeId, setSelectedReassignEmployeeId] =
    useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const [formData, setFormData] = useState({
    clientSource: "CAMPAIGN",
    clientStatus: "AVAILABLE",
    email: "",
    phoneNumber: "",
    alternatePhone: "",
    companyName: "",
    industry: "",
    website: "",
    notes: "",
    tags: "",
    clientScore: 0,
    assignedToId: "",
    accountType: "CASH",
    gstNumber: "",
    panNumber: "",
    billingAddress: "",
    shippingAddress: "",
    creditLimit: 0,
    paymentTerms: "",
    status: "ENABLED",
    contactPersons: [
      {
        contactPersonName: "",
        email: "",
        whatsAppNumber: "",
        mobileNumber: "",
      },
    ],
  });

  const [bulkClients, setBulkClients] = useState([]);
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const isSalesManager = user?.authorities?.includes("sales-manager");

  // Client Sources and Account Types
  const clientSources = [
    "CAMPAIGN",
    "REFERRAL",
    "INBOUND",
    "OUTBOUND",
    "OTHER",
  ];
  const accountTypes = ["CASH", "CREDIT"];

  // Helper function to format text (First letter uppercase, rest lowercase)
  const formatText = (text) => {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  // Transform data for API submission
  const transformFormDataForAPI = (data) => {
    return {
      ...data,
      contactPersons: data.contactPersons.map((contact) => ({
        ...contact,
        whatsAppNumber: contact.whatsAppNumber
          ? parseInt(contact.whatsAppNumber, 10)
          : null,
        mobileNumber: contact.mobileNumber
          ? parseInt(contact.mobileNumber, 10)
          : null,
      })),
    };
  };

  // Transform API data for form
  const transformAPIDataForForm = (client) => {
    return {
      ...client,
      contactPersons: client.contactPersons?.map((contact) => ({
        ...contact,
        whatsAppNumber: contact.whatsAppNumber
          ? String(contact.whatsAppNumber)
          : "",
        mobileNumber: contact.mobileNumber ? String(contact.mobileNumber) : "",
      })) || [
        {
          contactPersonName: "",
          email: "",
          whatsAppNumber: "",
          mobileNumber: "",
        },
      ],
    };
  };

  const validateContactPersons = (contactPersons) => {
    for (let i = 0; i < contactPersons.length; i++) {
      const contact = contactPersons[i];

      // 🔴 Mandatory mobile number
      if (!contact.mobileNumber) {
        toast.error(`Contact person ${i + 1}: Mobile number is mandatory`);
        return false;
      }

      if (contact.mobileNumber.length < 10) {
        toast.error(
          `Contact person ${i + 1}: Mobile number must be at least 10 digits`,
        );
        return false;
      }

      if (contact.whatsAppNumber && contact.whatsAppNumber.length < 10) {
        toast.error(
          `Contact person ${i + 1}: WhatsApp number must be at least 10 digits`,
        );
        return false;
      }
    }
    return true;
  };

  const fetchClients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = isSalesManager
        ? `${process.env.REACT_APP_SALES_MANAGER}/clients/assigned`
        : `${process.env.REACT_APP_SALES}/clients/assigned`;

      const response = await axiosInstance.get(endpoint);

      if (response.data.status === 200 && response.data.data) {
        setClients(response.data.data);
      } else {
        setError("Failed to fetch clients");
      }
    } catch (err) {
      console.log(err.response?.data?.message || "Failed to fetch clients");
    } finally {
      setLoading(false);
    }
  }, [isSalesManager]);

  useEffect(() => {
    if (user) {
      fetchClients();
    }
  }, [user, fetchClients]);

  useEffect(() => {
    if (!isSalesManager && user?.empId) {
      setFormData((prev) => ({
        ...prev,
        assignedToId: user.empId,
      }));
    }
  }, [isSalesManager, user]);

  const fetchEmployees = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/employees/sales/list`,
      );
      if (response.data.status === 200 && response.data.data) {
        setEmployees(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching employees:", err);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const createClient = async (clientData) => {
    setLoading(true);
    try {
      const endpoint = isSalesManager
        ? `${process.env.REACT_APP_SALES_MANAGER}/clients`
        : `${process.env.REACT_APP_SALES}/clients`;

      const transformedData = transformFormDataForAPI(clientData);
      console.log("Create Client request:", transformedData);

      const response = await axiosInstance.post(endpoint, transformedData);

      if (response.status === 200) {
        await fetchClients();
        toast.success("Client created successfully!");
        resetForm();
        setShowAddForm(false);
      } else {
        setError("Failed to create client");
      }
    } catch (err) {
      console.error("Error creating client:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSalesManager && !formData.assignedToId) {
      toast.info("Please assign this client to an employee!");
      return;
    }

    if (!validateContactPersons(formData.contactPersons)) {
      return;
    }

    if (editingClientId) {
      await updateClient({ ...formData, id: editingClientId });
    } else {
      await createClient(formData);
    }
  };

  const updateClient = async (clientData) => {
    try {
      setLoading(true);
      const endpoint = isSalesManager
        ? `${process.env.REACT_APP_SALES_MANAGER}/clients`
        : `${process.env.REACT_APP_SALES}/clients`;

      const transformedData = transformFormDataForAPI(clientData);

      const response = await axiosInstance.put(endpoint, transformedData);
      if (response.data.status === 200) {
        await fetchClients();
        toast.success("Client updated successfully!");
        setEditingClientId(null);
        resetForm();
        setShowAddForm(false);
      }
      return response.data;
    } catch (error) {
      console.error("Error updating client:", error);
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      setLoading(true);
      const endpoint = isSalesManager
        ? `${process.env.REACT_APP_SALES_MANAGER}/client/template/crm/download-template`
        : `${process.env.REACT_APP_SALES}/client/template/crm/download-template`;

      const response = await axiosInstance.get(endpoint, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "client_upload_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Template downloaded successfully!");
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      if (validTypes.includes(file.type)) {
        setSelectedFile(file);
        setUploadSuccess(false);
      } else {
        toast.error("Please select a valid Excel file (.xlsx or .xls)");
        e.target.value = null;
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file to upload");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("file", selectedFile);

      const endpoint = isSalesManager
        ? `${process.env.REACT_APP_SALES_MANAGER}/client/template/crm/upload`
        : `${process.env.REACT_APP_SALES}/client/template/crm/upload`;

      const response = await axiosInstance.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.status === 200) {
        setUploadSuccess(true);
        toast.success("Clients uploaded successfully!");

        setTimeout(() => {
          setShowUploadModal(false);
          setSelectedFile(null);
        }, 2000);

        fetchClients();
      }
    } catch (error) {
      console.error("Error uploading file:", error);
    } finally {
      setLoading(false);
    }
  };

  const assignClientToEmployee = async (clientId, employeeId) => {
    try {
      const endpoint = isSalesManager
        ? `${process.env.REACT_APP_SALES_MANAGER}/clients/assign/${clientId}/employee/${employeeId}`
        : `${process.env.REACT_APP_SALES}/clients/assign/${clientId}/employee/${employeeId}`;

      const response = await axiosInstance.put(endpoint);

      if (response.data.status === 200) {
        await fetchClients();
        toast.success("Client assigned successfully!");
      }

      return response.data;
    } catch (error) {
      console.error("Error assigning client:", error);
      throw error;
    }
  };

  const handleReassignClick = (clientId) => {
    setReassignClientId(clientId);
    setSelectedReassignEmployeeId("");
    setShowReassignModal(true);
  };

  const handleReassignSubmit = async () => {
    if (!selectedReassignEmployeeId) {
      toast.error("Please select an employee");
      return;
    }
    try {
      await assignClientToEmployee(
        reassignClientId,
        parseInt(selectedReassignEmployeeId),
      );
      setShowReassignModal(false);
      setReassignClientId(null);
    } catch (err) {
      console.error("Reassign failed:", err);
    }
  };

  const renderActionButtons = (client) => (
    <div className="action-buttons">
      <IconButton
        icon="mdi:account-switch"
        onClick={(e) => {
          e.stopPropagation();
          handleReassignClick(client.id);
        }}
        variant="edit"
        title="Reassign Client"
      />
      <IconButton
        icon="iconamoon:edit-light"
        onClick={(e) => {
          e.stopPropagation();
          handleEditClient(client);
        }}
        variant="edit"
        title="Edit Client"
      />
      <IconButton
        icon="mdi:delete"
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteClick(client.id);
        }}
        variant="delete"
        title="Delete Client"
      />
    </div>
  );

  const handleEditClient = (client) => {
    if (!client || !client.id) {
      toast.error("Invalid client selected");
      return;
    }

    const transformedClient = transformAPIDataForForm(client);

    setFormData({
      ...formData,
      ...transformedClient,
      assignedToId: transformedClient.assignedToId || "",
    });

    setEditingClientId(client.id);
    setShowAddForm(true);
  };

  const handleSaveAssignment = async (clientId, employeeId) => {
    try {
      if (!clientId || !employeeId) {
        toast.error("Invalid client or employee selection");
        return;
      }
      await assignClientToEmployee(clientId, employeeId);
    } catch (error) {
      console.error("Failed to assign client:", error);
    }
  };

  const handleDeleteClient = async () => {
    if (!clientToDelete) return;

    try {
      setLoading(true);
      const endpoint = isSalesManager
        ? `${process.env.REACT_APP_SALES_MANAGER}/clients/${clientToDelete}`
        : `${process.env.REACT_APP_SALES}/clients/${clientToDelete}`;

      const response = await axiosInstance.delete(endpoint);

      if (response.data.status === 200) {
        await fetchClients();
        toast.success("Client deleted successfully!");
      }
    } catch (error) {
      console.error("Error deleting client:", error);
    } finally {
      setLoading(false);
      setShowWarningPopup(false);
      setClientToDelete(null);
    }
  };

  const createBulkClients = async () => {
    setLoading(true);
    try {
      const endpoint = isSalesManager
        ? `${process.env.REACT_APP_SALES_MANAGER}/clients/bulk`
        : `${process.env.REACT_APP_SALES}/clients/bulk`;

      const response = await axiosInstance.post(endpoint, bulkClients);
      if (response.data.status === 200) {
        await fetchClients();
        setBulkClients([]);
        toast.success("Bulk clients created successfully!");
      }
    } catch (err) {
      console.log(
        err.response?.data?.message || "Failed to create bulk clients",
      );
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      clientSource: "CAMPAIGN",
      clientStatus: "AVAILABLE",
      email: "",
      phoneNumber: "",
      alternatePhone: "",
      companyName: "",
      industry: "",
      website: "",
      notes: "",
      tags: "",
      clientScore: 0,
      assignedToId: "",
      accountType: "CASH",
      gstNumber: "",
      panNumber: "",
      billingAddress: "",
      shippingAddress: "",
      creditLimit: 0,
      paymentTerms: "",
      status: "ENABLED",
      contactPersons: [
        {
          contactPersonName: "",
          email: "",
          whatsAppNumber: "",
          mobileNumber: "",
        },
      ],
    });
  };

  const handleDeleteClick = (clientId) => {
    setClientToDelete(clientId);
    setShowWarningPopup(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "assignedToId" ? Number(value) : value,
    }));
  };

  const renderCompanyWithSl = (value, client) => {
    return (
      <span>
        {client.slNo}. {value || "—"}
      </span>
    );
  };

  // Custom cell renderers for the table
  const renderEmailCell = (email) => {
    if (!email) return "";
    return (
      <a
        href={`mailto:${email}`}
        className="email-link"
        onClick={(e) => e.stopPropagation()}
      >
        {email}
      </a>
    );
  };

  const renderWebsiteCell = (website) => {
    if (!website) return "";
    const url = website.startsWith("http") ? website : `https://${website}`;
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="website-link"
        onClick={(e) => e.stopPropagation()}
      >
        {website}
      </a>
    );
  };

  const renderStatusCard = (value, type) => {
    if (!value) return "";
    const formattedValue = formatText(value);
    const className = `status-card ${type}-status ${value.toLowerCase()}`;
    return <span className={className}>{formattedValue}</span>;
  };

  const renderTagsCell = (tags) => {
    if (!tags) return "—";
    const tagArray = tags
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag);
    if (tagArray.length === 0) return "—";

    return (
      <div className="tags-container">
        {tagArray.map((tag, index) => (
          <span key={index} className="tag-chip">
            {tag}
          </span>
        ))}
      </div>
    );
  };

  const renderScoreCell = (score) => {
    if (score === null || score === undefined) return "—";

    const numScore = Number(score);
    const maxStars = 5;
    const filledStars = Math.round(numScore);

    let scoreClass = "score-low";
    if (numScore >= 4) scoreClass = "score-high";
    else if (numScore >= 3) scoreClass = "score-medium";

    return (
      <div className="score-container">
        <div className="score-stars">
          {Array.from({ length: maxStars }, (_, index) => (
            <span
              key={index}
              className={`star ${
                index < filledStars ? "filled" : "empty"
              } ${scoreClass}`}
            >
              ★
            </span>
          ))}
        </div>
      </div>
    );
  };

  const renderAssignedToColumn = (client) => {
    if (isSalesManager) {
      return (
        <select
          className="assigned-to-select"
          value={client.assignedToId || ""}
          onChange={(e) => {
            if (e.target.value) {
              handleSaveAssignment(client.id, parseInt(e.target.value));
            }
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <option value="">Unassigned</option>
          {employees.map((emp) => (
            <option key={emp.empId} value={emp.empId}>
              {emp.firstName} {emp.lastName}
            </option>
          ))}
        </select>
      );
    }
    return <span>{client.assignedToName || "—"}</span>;
  };

  const allClientsHeaders = [
    {
      label: "Company Name",
      key: "companyName",
      render: renderCompanyWithSl,
    },
    {
      label: "Email",
      key: "email",
      render: renderEmailCell,
    },
    { label: "Phone", key: "phoneNumber" },
    { label: "Industry", key: "industry" },
    {
      label: "Website",
      key: "website",
      render: renderWebsiteCell,
    },
    {
      label: "Account Type",
      key: "accountType",
      render: (value) => renderStatusCard(value, "account-type"),
    },
    {
      label: "Source",
      key: "clientSource",
      render: (value) => renderStatusCard(value, "source"),
    },
    {
      label: "Tags",
      key: "tags",
      render: renderTagsCell,
    },
    {
      label: "Score",
      key: "clientScore",
      render: renderScoreCell,
    },
    {
      label: "Assigned To",
      key: "assignedTo",
      render: (value, client) => renderAssignedToColumn(client),
    },
    { label: "Notes", key: "notes" },
    {
      label: "Actions",
      key: "actions",
      render: (value, client) => renderActionButtons(client),
      type: "action",
      actionType: "custom",
    },
  ];

  const allClientsColumns = allClientsHeaders.map((col) => ({
    header: col.label,
    accessor: col.key,
    render: col.render,
    type: col.type,
    actionType: col.actionType,
  }));

  const allClientsData = clients.map((client, index) => ({
    ...client,
    slNo: index + 1,
    actions: client,
  }));

  const allClientsDataSafe = Array.isArray(allClientsData)
    ? allClientsData
    : [];

  if (loading && clients.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div className="leads-container">
      <div className="header">
        <div className="header-left">
          <h1>Sales Clients Management</h1>
          <p className="subtitle">
            Manage and monitor all client interactions and accounts effectively.
          </p>
        </div>
        <div className="header-actions">
          <ButtonWithIcon
            icon="mdi:plus"
            text="Add New Client"
            className="submit-button"
            onClick={() => {
              resetForm();
              setEditingClientId(null);
              setShowAddForm(true);
            }}
          />

          <ButtonWithIcon
            icon="mdi:file-upload"
            text="Upload Excel"
            onClick={() => setShowUploadModal(true)}
          />

          <ButtonWithIcon
            icon="tdesign:send"
            text={`Import ${bulkClients.length} Clients`}
            onClick={createBulkClients}
            disabled={bulkClients.length === 0 || loading}
          />
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-error">
            ×
          </button>
        </div>
      )}

      {showUploadModal && (
        <div className="upload-modal-overlay">
          <div className="upload-modal">
            <div className="modal-header">
              <h3>Bulk Upload Client Details</h3>
              <button
                className="close-button"
                onClick={() => setShowUploadModal(false)}
                aria-label="Close modal"
              >
                &times;
              </button>
            </div>

            <div className="modal-subheader">
              <p>
                Download the template, fill in the details, and upload the file
                below.
              </p>
            </div>

            <div className="modal-body">
              {uploadSuccess ? (
                <div className="success-message">
                  <i className="fas fa-check-circle success-icon"></i>
                  <p>File uploaded successfully!</p>
                </div>
              ) : (
                <>
                  <button
                    className="download-template-button"
                    onClick={downloadTemplate}
                  >
                    <i className="fas fa-download"></i> Download Template
                  </button>

                  <div className="file-input-container">
                    <label
                      htmlFor="client-file-upload"
                      className="file-upload-label"
                    >
                      <i className="fas fa-cloud-upload-alt"></i>
                      {selectedFile
                        ? selectedFile.name
                        : "Click to choose Excel file"}
                    </label>
                    <input
                      id="client-file-upload"
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      className="file-input"
                    />
                  </div>

                  <button
                    className="upload-button"
                    onClick={handleUpload}
                    disabled={!selectedFile || loading}
                  >
                    <i className="fas fa-upload"></i>{" "}
                    {loading ? "Uploading..." : "Upload File"}
                  </button>

                  <button
                    className="cancel-button"
                    onClick={() => setShowUploadModal(false)}
                  >
                    <i className="fas fa-times-circle"></i> Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Universal Create/Edit Form */}
      {showAddForm && (
        <div className="add-form-container">
          <div className="form-header">
            <h3>{editingClientId ? "Edit Client" : "Add New Client"}</h3>
          </div>
          <form onSubmit={handleSubmit} className="compact-form">
            {/* Row 1: Company Name, Email, Phone, Industry, Website, Alternate Phone */}
            <div className="form-row">
              <div className="form-group compact">
                <label>Company Name *</label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group compact">
                <label>Company Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group compact">
                <label>Phone</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group compact">
                <label>Industry</label>
                <input
                  type="text"
                  name="industry"
                  value={formData.industry}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group compact">
                <label>Website</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group compact">
                <label>Alternate Phone</label>
                <input
                  type="tel"
                  name="alternatePhone"
                  value={formData.alternatePhone}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Row 2: GST, PAN, Credit Limit, Payment Terms, Assigned To (conditional), Tags */}
            <div className="form-row">
              <div className="form-group compact">
                <label>GST Number</label>
                <input
                  type="text"
                  name="gstNumber"
                  value={formData.gstNumber}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group compact">
                <label>PAN Number</label>
                <input
                  type="text"
                  name="panNumber"
                  value={formData.panNumber}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group compact">
                <label>Credit Limit</label>
                <input
                  type="number"
                  name="creditLimit"
                  value={formData.creditLimit}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group compact">
                <label>Payment Terms</label>
                <input
                  type="text"
                  name="paymentTerms"
                  value={formData.paymentTerms}
                  onChange={handleInputChange}
                  placeholder="e.g., Net 30"
                />
              </div>
              {isSalesManager && (
                <div className="form-group compact">
                  <label>Assigned To</label>
                  <select
                    name="assignedToId"
                    value={formData.assignedToId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Employee</option>
                    {employees.map((emp) => (
                      <option key={emp.empId} value={emp.empId}>
                        {emp.firstName} {emp.lastName}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div
                className={`form-group compact ${
                  isSalesManager ? "" : "span-2"
                }`}
              >
                <label>Tags</label>
                <input
                  type="text"
                  name="tags"
                  value={formData.tags}
                  onChange={handleInputChange}
                  placeholder="Comma separated tags"
                />
              </div>
            </div>

            {/* Row 3: Account Type, Source, Score */}
            <div className="form-row three-cols">
              <div className="form-group compact">
                <label>Account Type *</label>
                <div className="button-group compact">
                  {accountTypes.map((type) => (
                    <button
                      key={type}
                      type="button"
                      className={`btn-option ${
                        formData.accountType === type ? "active" : ""
                      }`}
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, accountType: type }))
                      }
                    >
                      {formatText(type)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group compact">
                <label>Source *</label>
                <select
                  name="clientSource"
                  value={formData.clientSource}
                  onChange={handleInputChange}
                  required
                >
                  {clientSources.map((source) => (
                    <option key={source} value={source}>
                      {formatText(source)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group compact">
                <label>Score</label>
                <div className="score-selector">
                  {Array.from({ length: 5 }, (_, i) => i + 1).map((score) => (
                    <button
                      key={score}
                      type="button"
                      className={`star-button ${
                        formData.clientScore >= score ? "active" : ""
                      }`}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          clientScore: prev.clientScore === score ? 0 : score,
                        }))
                      }
                      title={`${score} star${score > 1 ? "s" : ""}`}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Row 4: Billing Address, Shipping Address, Notes */}
            <div className="form-row three-cols">
              <div className="form-group compact">
                <label>Billing Address</label>
                <textarea
                  name="billingAddress"
                  value={formData.billingAddress}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>
              <div className="form-group compact">
                <div className="label-with-checkbox">
                  <label>Shipping Address</label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData((prev) => ({
                            ...prev,
                            shippingAddress: prev.billingAddress,
                          }));
                        }
                      }}
                    />
                    <span>Same as billing</span>
                  </label>
                </div>
                <textarea
                  name="shippingAddress"
                  value={formData.shippingAddress}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>
              <div className="form-group compact">
                <label>Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Additional notes about this client..."
                />
              </div>
            </div>

            {/* Contact Persons Section */}
            <div className="contact-form">
              <div className="contact-header">
                <h6>Contact Information</h6>
                <ButtonWithIcon
                  type="button"
                  icon="mdi:account-plus"
                  text="Add Contact"
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      contactPersons: [
                        ...prev.contactPersons,
                        {
                          contactPersonName: "",
                          email: "",
                          whatsAppNumber: "",
                          mobileNumber: "",
                        },
                      ],
                    }));
                  }}
                  variant="secondary"
                  size="sm"
                />
              </div>

              {formData.contactPersons.map((contact, index) => (
                <div key={index} className="contact-person-row">
                  <div className="contact-person-header">
                    {formData.contactPersons.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger remove-contact"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            contactPersons: prev.contactPersons.filter(
                              (_, i) => i !== index,
                            ),
                          }));
                        }}
                        title="Remove contact person"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <div className="form-row">
                    <div className="form-group compact">
                      <input
                        type="text"
                        placeholder="Contact Name"
                        value={contact.contactPersonName}
                        onChange={(e) => {
                          const newContacts = [...formData.contactPersons];
                          newContacts[index].contactPersonName = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            contactPersons: newContacts,
                          }));
                        }}
                      />
                    </div>
                    <div className="form-group compact">
                      <input
                        type="email"
                        placeholder="Email"
                        value={contact.email}
                        onChange={(e) => {
                          const newContacts = [...formData.contactPersons];
                          newContacts[index].email = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            contactPersons: newContacts,
                          }));
                        }}
                      />
                    </div>
                    <div className="form-group compact">
                      <input
                        type="tel"
                        placeholder="Mobile Number *"
                        value={contact.mobileNumber}
                        onChange={(e) => {
                          const newContacts = [...formData.contactPersons];
                          newContacts[index].mobileNumber =
                            e.target.value.replace(/\D/g, "");
                          setFormData((prev) => ({
                            ...prev,
                            contactPersons: newContacts,
                          }));
                        }}
                        maxLength="15"
                        required
                      />
                    </div>
                    <div className="form-group compact whatsapp-copy">
                      <input
                        type="tel"
                        placeholder="WhatsApp Number"
                        value={contact.whatsAppNumber}
                        onChange={(e) => {
                          const newContacts = [...formData.contactPersons];
                          newContacts[index].whatsAppNumber =
                            e.target.value.replace(/\D/g, "");
                          setFormData((prev) => ({
                            ...prev,
                            contactPersons: newContacts,
                          }));
                        }}
                        maxLength="15"
                      />

                      <button
                        type="button"
                        className="copy-btn"
                        title="Copy mobile number to WhatsApp"
                        onClick={() => {
                          const newContacts = [...formData.contactPersons];
                          newContacts[index].whatsAppNumber =
                            newContacts[index].mobileNumber || "";
                          setFormData((prev) => ({
                            ...prev,
                            contactPersons: newContacts,
                          }));
                        }}
                      >
                        <Icon icon="mdi:content-copy" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="form-actions">
              <ButtonWithIcon
                type="button"
                icon="mdi:cancel"
                text="Cancel"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingClientId(null);
                  resetForm();
                }}
                className="cancel-button"
              />
              <ButtonWithIcon
                type="submit"
                icon="tdesign:send"
                className="submit-button"
                text={
                  editingClientId
                    ? loading
                      ? "Updating..."
                      : "Update Client"
                    : loading
                      ? "Creating..."
                      : "Create Client"
                }
                disabled={loading}
              />
            </div>
          </form>
        </div>
      )}

      <div className="tab-content">
        <GeneralTable
          columns={allClientsColumns}
          data={allClientsDataSafe}
          title={isSalesManager ? "All Clients" : "My Assigned Clients"}
        />
      </div>

      <WarningPopup
        isOpen={showWarningPopup}
        onClose={() => setShowWarningPopup(false)}
        onAccept={handleDeleteClient}
        onDecline={() => {
          setShowWarningPopup(false);
          setClientToDelete(null);
        }}
        title="Delete Client"
        message="Are you sure you want to delete this client? This action cannot be undone."
        acceptText="Yes, Delete"
        declineText="Cancel"
      />

      {showReassignModal && (
        <div className="upload-modal-overlay">
          <div className="upload-modal">
            <div className="modal-header">
              <h3>Reassign Client</h3>
              <button
                className="close-button"
                onClick={() => setShowReassignModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>Select an employee to reassign this client to:</p>
              <select
                className="assigned-to-select"
                value={selectedReassignEmployeeId}
                onChange={(e) => setSelectedReassignEmployeeId(e.target.value)}
              >
                <option value="">-- Select Employee --</option>
                {employees
                  .filter((emp) => emp.empId !== user?.empId)
                  .map((emp) => (
                    <option key={emp.empId} value={emp.empId}>
                      {emp.firstName} {emp.lastName} — {emp.companyEmail}
                    </option>
                  ))}
              </select>
              <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
                <ButtonWithIcon
                  icon="tdesign:send"
                  text="Reassign"
                  className="submit-button"
                  onClick={handleReassignSubmit}
                  disabled={loading}
                />
                <ButtonWithIcon
                  icon="mdi:cancel"
                  text="Cancel"
                  className="cancel-button"
                  onClick={() => setShowReassignModal(false)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
